import {
  FormFieldHelperText,
  Headline,
  RadiobuttonCard,
  RadiobuttonCardGroup,
  Text,
} from '@velocity/ui';
import React, { useId } from 'react';
import { FormRadioOption } from './form-radio-field';

export interface FormRadioCardFieldProps {
  name: string;
  /** Question shown above the cards (Figma: headline-200) */
  label: React.ReactNode;
  options: ReadonlyArray<FormRadioOption>;
  value?: string | null;
  onChange: (value: string) => void;
  onBlur?: () => void;
  errorMessage?: string;
  required?: boolean;
  disabled?: boolean;
  dataTestId?: string;
  className?: string;
}

/*
 * Stacked Velocity `RadiobuttonCard`s under a headline
 * (Figma: Instant Lease - B2C, node 708:7382 → "formkit/radiobutton-card",
 * 308px wide, 20px/16px padding, 8px between the cards).
 * Controlled so it can be used with react-hook-form's `Controller`.
 */
const FormRadioCardField: React.FC<FormRadioCardFieldProps> = ({
  name,
  label,
  options,
  value,
  onChange,
  onBlur,
  errorMessage,
  required,
  disabled,
  dataTestId,
  className = '',
}) => {
  const labelId = useId();

  return (
    <div className={`flex w-full flex-col gap-6 ${className}`}>
      <Headline
        variant='200'
        component='p'
        id={labelId}
        className='text-(--vel-color-secondary-400)!'
      >
        {label}
      </Headline>

      <RadiobuttonCardGroup
        name={name}
        value={value ?? ''}
        onChange={(event) => onChange(event.target.value)}
        onBlur={onBlur}
        required={required}
        disabled={disabled}
        error={!!errorMessage}
        aria-labelledby={labelId}
        data-testid={dataTestId ?? `radio-field-${name}`}
      >
        {options.map((option) => (
          <RadiobuttonCard
            key={option.value}
            value={option.value}
            disabled={option.disabled}
            details={
              <Text
                component='span'
                variant='200'
                className='text-(--vel-color-secondary-400)!'
              >
                {option.label}
              </Text>
            }
            className='w-full! max-w-77 px-5! py-4!'
            data-testid={`radio-${name}-${option.value}`}
          />
        ))}
      </RadiobuttonCardGroup>

      {errorMessage && (
        <FormFieldHelperText kind='error' withIcon live>
          {errorMessage}
        </FormFieldHelperText>
      )}
    </div>
  );
};

export default FormRadioCardField;
